const RIGHTS_INVITE_PREFIX = "/rights/invite/";
const RIGHTS_INVITE_TOKEN_RE = /^[A-Za-z0-9_-]{16,}$/;

export function normalizeInternalRedirect(value: string | null | undefined): string | null {
  if (!value) return null;
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    decoded = value;
  }
  if (!decoded.startsWith("/") || decoded.startsWith("//")) return null;
  return decoded;
}

export function isRightsInviteRedirect(value: string | null | undefined): boolean {
  const redirect = normalizeInternalRedirect(value);
  return Boolean(redirect && redirect.startsWith(RIGHTS_INVITE_PREFIX));
}

export function normalizeRightsInviteToken(value: string | null | undefined): string | null {
  const token = value?.trim();
  if (!token || !RIGHTS_INVITE_TOKEN_RE.test(token)) return null;
  return token;
}

export function buildRightsInviteOnboardingUrl(token: string): string {
  return `/onboarding?rights_invite=${encodeURIComponent(token)}`;
}

export function buildRootLoginUrl(redirectPath: string): string {
  return `/?redirect=${encodeURIComponent(redirectPath)}`;
}

export function buildSignupUrl(options: {
  side?: "buyer" | "seller";
  redirectPath?: string | null;
  rightsInviteToken?: string | null;
}): string {
  const params = new URLSearchParams();
  if (options.side) params.set("side", options.side);
  if (options.rightsInviteToken) params.set("rights_invite", options.rightsInviteToken);
  if (options.redirectPath) params.set("redirect", options.redirectPath);
  const query = params.toString();
  return query ? `/auth/signup?${query}` : "/auth/signup";
}

/**
 * Route prefixes a post-onboarding return is allowed to land on. An allowlist rather than a
 * denylist: a new public route should not silently become a redirect target. `/onboarding` is
 * deliberately absent — returning there would loop.
 */
const RETURN_TO_ALLOWED_PREFIXES = [
  "/licenses",
  "/projects",
  "/tracks",
  "/albums",
  "/artists",
  "/playlists",
  "/playlist/",
  "/review/",
  "/search",
  "/storefront/",
  "/marketplace",
  "/home",
  "/collections",
  "/profile",
  "/settings",
  "/assistant",
  "/track/",
  "/shared/",
  // The rest of the OnboardingGuard-protected surface. Omitting these silently dropped the
  // destination for anyone deep-linked to them while still in draft — a narrower recurrence of
  // the very bug this mechanism exists to fix. Every entry here is already behind AuthGuard and
  // its own capability gate, so admitting it as a return target grants nothing new.
  "/upload",
  "/drafts",
  "/request-song",
  "/nda",
  // The NDA page's old URL: it now redirects to /nda, and NDA emails and notifications sent
  // before the move still link here.
  "/confidential",
  "/rights-holders",
  "/rights-to-confirm",
  "/publishers",
  "/search-legacy",
  "/admin",
] as const;

/**
 * Sentinel origin used only to give the URL parser something to resolve a relative path
 * against. Never dereferenced, never sent anywhere — just a fixed marker we can compare
 * `url.origin` back to, to detect that resolution escaped to a different origin.
 */
const RETURN_TO_SENTINEL_ORIGIN = "https://internal.invalid";

/**
 * Validate a `returnTo` value into a same-origin path, or null if it is not safe.
 *
 * This used to be two rounds of substring matching (`includes("\\")`, `split("/").includes("..")`)
 * over the raw string, and both rounds were bypassed: `..`, `\`, `.%2e`, `%2e.`, and `%2e%2e`
 * (ASCII case-insensitive) are ALL "go up a directory" to a browser's URL parser, but none of
 * them read as the literal text ".." to a substring check — and a SECOND layer of percent-encoding
 * (`%252e%252e`) defeats it completely, because `normalizeInternalRedirect` only decodes once, so
 * the payload survives as the inert-looking literal text `%2e%2e`.
 *
 * The fix is to stop guessing at string patterns and instead run the exact algorithm a browser
 * will use to actually navigate: resolve the value with the URL parser, then judge the RESOLVED
 * pathname against the allowlist. This closes the entire bypass class at once, including forms
 * nobody has enumerated yet — do not "simplify" this back to string matching.
 */
export function safeReturnTo(value: string | null | undefined): string | null {
  const path = normalizeInternalRedirect(value);
  if (!path) return null;

  // Control characters can be used to split the header/URL parsing of downstream consumers, and
  // the URL parser below silently strips tab/newline rather than rejecting them outright — so
  // this check must run before resolution, not be folded into it.
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(path)) return null;

  // Browsers normalize `\` to `/` anywhere in an http(s) path, so a backslash anywhere could
  // smuggle a traversal segment or a host past the checks below. No legitimate internal route
  // contains one. Kept as defence in depth alongside the origin check below, which would also
  // catch most backslash-smuggled hosts on its own.
  if (path.includes("\\")) return null;

  // Resolve against a sentinel origin using the real URL parser, then judge the RESOLVED
  // pathname — never the raw input — against the allowlist.
  let url: URL;
  try {
    url = new URL(path, RETURN_TO_SENTINEL_ORIGIN);
  } catch {
    return null;
  }
  // Anything that escaped the sentinel origin (protocol-relative `//host`, a backslash-smuggled
  // host, a scheme change, etc.) is unsafe regardless of what its pathname looks like. Kept as
  // defence in depth: the checks above should already catch these forms, but if one of them is
  // ever weakened, this is the backstop that still refuses to hand back a foreign destination.
  if (url.origin !== RETURN_TO_SENTINEL_ORIGIN) return null;

  // Reject a still-percent-encoded dot, slash, or backslash left over in the RESOLVED pathname.
  // `normalizeInternalRedirect` decodes exactly once, and the URL parser above only collapses a
  // dot-segment when a whole path segment is literally "..", ".%2e", "%2e.", or "%2e%2e" — an
  // encoded token embedded mid-segment (e.g. a doubly-encoded "%252e" that decodes to the
  // literal text "%2e" sitting inside "report%2ehtml") survives resolution untouched and would
  // slip past the allowlist unnoticed, because the allowlist only inspects prefixes. Without this
  // check, safeReturnTo("/tracks/report%252ehtml") returns "/tracks/report%2ehtml" — a value
  // that still means something different after one more `decodeURIComponent`. No legitimate
  // internal route needs an encoded dot/slash/backslash in its path — IDs are UUIDs, share
  // tokens are alphanumeric, slugs cannot contain a slash — so rejecting these here removes the
  // "rejects its own output" class of non-idempotence: for every accepted output, decoding it
  // once more can no longer reveal a hidden traversal segment.
  if (/%2e|%2f|%5c/i.test(url.pathname)) return null;

  const allowed = RETURN_TO_ALLOWED_PREFIXES.some(
    (prefix) =>
      prefix.endsWith("/")
        ? url.pathname.startsWith(prefix)
        : url.pathname === prefix || url.pathname.startsWith(`${prefix}/`),
  );
  if (!allowed) return null;

  // The fragment (url.hash) is deliberately dropped, even though the path/query were validated.
  // src/App.tsx reads window.location.hash on mount and, when it contains `type=recovery`,
  // navigates to /auth/update-password carrying the hash along; src/auth/update-password.tsx
  // then pulls access_token/refresh_token out of that hash and calls supabase.auth.setSession().
  // A returnTo value with a crafted fragment (e.g. "/tracks#type=recovery&access_token=X&...")
  // would ride through unvalidated and hand the victim's browser an attacker's session — this is
  // session fixation, not a cosmetic issue. No legitimate return destination in this app needs a
  // fragment, so it is never echoed. Do not add `+ url.hash` back here.
  return url.pathname + url.search;
}

/** Build the onboarding URL that carries a validated destination through the wizard. */
export function buildOnboardingUrlWithReturn(returnPath: string): string {
  return `/onboarding?returnTo=${encodeURIComponent(returnPath)}`;
}
